import React from "react";
import { Zap } from "lucide-react";

/**
 * Compact reusable preview card used on both the NewProject page and
 * the EditProjectModal to summarise the currently-attached (or about
 * to be attached) design: DC capacity, module count + wattage + model,
 * inverter model + count, and annual yield.
 *
 * Accepts a flat `data` object shaped like the Design model
 * (`systemSize`, `numberOfModules`, `moduleWattage`, `moduleModel`,
 * `inverterModel`, `inverterCount`, `annualEnergyProduction`, `source`).
 * When any of these fields is missing they are gracefully rendered as
 * "—" so partial imports still yield something readable.
 */
export default function DesignPreviewCard({ data, sourceHint, testid = "np-import-preview" }) {
  if (!data) return null;
  const {
    systemSize, numberOfModules, moduleWattage, moduleModel,
    inverterModel, inverterCount, annualEnergyProduction, source,
  } = data;

  // Any signal that this design has SOMETHING to show. Otherwise skip.
  const isEmpty =
    !systemSize && !numberOfModules && !moduleWattage && !moduleModel
    && !inverterModel && !annualEnergyProduction;
  if (isEmpty) return null;

  const invCount = Number(inverterCount) || 0;
  return (
    <div
      className="border-l-2 border-[var(--accent-hover)] bg-[var(--surface)] p-4 rounded-r"
      data-testid={testid}
    >
      <div className="label mb-2">Design preview</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
        <div>
          <div className="muted font-mono uppercase text-[9px] tracking-wider">
            DC Capacity
          </div>
          <div
            className="font-display text-lg font-bold"
            style={{ color: "var(--accent-hover)" }}
            data-testid={`${testid}-kwp`}
          >
            {systemSize ? `${Number(systemSize).toFixed(2)} kWp` : "—"}
          </div>
          <div className="text-[10px] muted font-mono flex items-center gap-1 mt-0.5">
            <Zap size={9} /> DC nameplate
          </div>
        </div>
        <div>
          <div className="muted font-mono uppercase text-[9px] tracking-wider">
            Modules
          </div>
          <div className="font-display text-lg font-bold" data-testid={`${testid}-modules`}>
            {numberOfModules || "—"}
          </div>
          {moduleWattage ? (
            <div className="text-[10px] muted font-mono">
              {Number(moduleWattage).toFixed(0)} W each
            </div>
          ) : null}
          {moduleModel && (
            <div
              className="text-[10px] muted truncate mt-0.5"
              title={moduleModel}
              data-testid={`${testid}-module-model`}
            >
              {moduleModel}
            </div>
          )}
        </div>
        <div>
          <div className="muted font-mono uppercase text-[9px] tracking-wider">
            Inverter
          </div>
          <div
            className="font-medium text-sm truncate"
            title={inverterModel || ""}
            data-testid={`${testid}-inverter`}
          >
            {inverterModel || "—"}
          </div>
          {invCount > 0 && (
            <div
              className="text-[10px] muted font-mono mt-0.5"
              data-testid={`${testid}-inverter-count`}
            >
              × {invCount}
            </div>
          )}
        </div>
        <div>
          <div className="muted font-mono uppercase text-[9px] tracking-wider">
            Annual yield
          </div>
          <div className="font-medium text-sm" data-testid={`${testid}-yield`}>
            {annualEnergyProduction
              ? `${Number(annualEnergyProduction).toLocaleString()} kWh`
              : "—"}
          </div>
          {systemSize && annualEnergyProduction ? (
            <div className="text-[10px] muted font-mono mt-0.5">
              {(Number(annualEnergyProduction) / Number(systemSize)).toFixed(0)} kWh/kWp
            </div>
          ) : null}
        </div>
      </div>
      {(source || sourceHint) && (
        <div className="text-[10px] muted mt-3 font-mono">
          Source: {(source || sourceHint || "").replace(/_/g, " ")}
        </div>
      )}
    </div>
  );
}
